import { Dispatch } from '@reduxjs/toolkit'
import axios from 'axios'
import { format as formatDate } from 'date-fns'
import { format } from 'date-fns'
import _find from 'lodash/find'
import { toast } from 'react-toastify'

import unlock from '../../../public/favicon.png'
import cloud from '@/assets/img/cloud.svg'
import {
  setAppraisalOrderModalOpen,
  setAvmReportModalOpen,
  setDocDrawModalOpen,
  setPullCreditModalAction,
  setPullCreditModalOpen,
  setPullCreditModalUser,
  setTitleOrderDrawerOpen,
} from '@/client/generalSlice'
import { docExpiredAlertMessages } from '@/constants/alert'
import { ProfileTypes } from '@/constants/applicationProfile'
import { DEAL_SUMMARY_DOCUMENTS, THIRD_PARTY_REPORTS } from '@/constants/dealSummary'
import {
  DOCUMENT_CATEGORY,
  DOCUMENT_SOURCE,
  DOCUMENT_TAG,
  DOCUMENT_TYPE,
} from '@/constants/documents'
import { openFilesInMaxViewer } from '@/features/Documents/helper'
import { Application, OfferCalculationsType } from '@/graphql/generated'
import { baseApi } from '@/services'
import { baseQueryConfig } from '@/services/config'
import { Alerts, ApplicationProfile, Snapshot } from '@/types/unlock'
import { getThirdPartyReportPermissions } from '@/utils/dealSummary'
import { toastDefaultOptions } from '@/utils/toast'

export type DownloadOptionType = {
  title: string
  label: string
  downloadUrl?: string
  href?: URL
  disabled: boolean
  disabledMessage?: string
  fileName?: string
  documentType?: string
  onClick?: any
  functionClick?: boolean
  subOptions?: DownloadOptionType[]
}

export type DownloadItemsType = {
  title: string
  label: string
  icon: any
  top: number
  options: DownloadOptionType[]
  disabled?: boolean
}

export const getApplicationPdfName = (application: Application) => {
  const disclosureState = [
    'pending-app-submit',
    'pending-doc-upload',
    'pending-file-setup',
    'pending-condl-approval-uw',
    'pending-ie-delivery',
    'pending-intent-to-proceed',
  ]

  if (disclosureState.includes(application['applicationState'])) {
    return {
      fileName: `Disclosure - Initial Application for Unlock Agreement - ${formatDate(
        new Date(),
        'yyyy-MM-dd'
      )}.pdf`,
      documentType: 'initial-application-for-unlock-agreement',
    }
  }

  return {
    fileName: `Disclosure - Final Application for Unlock Agreement - ${formatDate(
      new Date(),
      'yyyy-MM-dd'
    )}.pdf`,
    documentType: 'final-application-for-unlock-agreement',
  }
}

export const requestPreliminaryFloodReport = async (externalId) => {
  try {
    toast.info('Requesting preliminary flood report.', { ...toastDefaultOptions })
    await makeRequestCorelogicPreliminaryFloodReport(externalId)
    toast.success('Preliminary flood report was returned correctly.', { ...toastDefaultOptions })
  } catch (error) {
    toast.error(
      error?.response?.data || 'An error occurred while requesting the preliminary flood report.',
      {
        ...toastDefaultOptions,
      }
    )
  }
}

const makeRequestCorelogicPreliminaryFloodReport = async (externalId) => {
  const headers = {
    Authorization: `JWT ${localStorage.getItem('jwt')}`,
  }

  await axios.post(
    `${baseQueryConfig.baseUrl}/v1/application/${externalId}/request-flood-report/`,
    {},
    {
      headers,
      withCredentials: true,
    }
  )
}

export const requestFloodZoneDeterminationReport = async (externalId) => {
  try {
    toast.info('Requesting flood zone determination order.', { ...toastDefaultOptions })
    await makeRequestCorelogicFloodZoneDeterminationReport(externalId)
    toast.success('Flood zone determination order was requested correctly.', {
      ...toastDefaultOptions,
    })
  } catch (error) {
    toast.error(
      error?.response?.data ||
        'An error occurred while requesting the flood zone determination order.',
      {
        ...toastDefaultOptions,
      }
    )
  }
}

const makeRequestCorelogicFloodZoneDeterminationReport = async (externalId) => {
  const headers = {
    Authorization: `JWT ${localStorage.getItem('jwt')}`,
  }

  await axios.post(
    `${baseQueryConfig.baseUrl}/v1/application/${externalId}/request-life-of-loan-flood-cert-report/`,
    {},
    {
      headers,
      withCredentials: true,
    }
  )
}

export const requestPropertyDetails = async (externalId, dispatch) => {
  const headers = {
    Authorization: `JWT ${localStorage.getItem('jwt')}`,
  }
  try {
    toast.info('Requesting Property Details.', { ...toastDefaultOptions })

    await axios.post(
      `${baseQueryConfig.baseUrl}/v1/application/${externalId}/request-property-details/`,
      {},
      {
        headers,
        withCredentials: true,
      }
    )
    // Invalidate cache for property details & flags
    dispatch(
      baseApi.util.invalidateTags([
        'PropertyDetails',
        'Application',
        'AtypicalityCheckV2',
        'AtypicalityCheckCalcs',
      ])
    )

    toast.success('Corelogic Property details was returned correctly.', { ...toastDefaultOptions })
  } catch (error) {
    toast.error(
      error?.response?.data || 'An error occurred while requesting the Property Details.',
      { ...toastDefaultOptions }
    )
  }
}

export const requestSampleContractDocs = async (
  externalId: string,
  generateSampleContractDocs: (externalId: string) => Promise<any>
) => {
  toast.info('Requesting sample contract documents.', { ...toastDefaultOptions })

  const response = await generateSampleContractDocs(externalId)

  if (response.error) {
    let errorMessage = 'There was an error generating sample contract docs.'

    if (Array.isArray(response.error)) {
      errorMessage = response.error.join('\n')
    }

    toast.error(errorMessage, { ...toastDefaultOptions })
  } else {
    toast.success('Sample contract docs are being generated.', { ...toastDefaultOptions })
  }
}

export default function dropDownMenuItems(
  application: Application,
  profiles: ApplicationProfile[],
  showIEButton: boolean,
  showICSButton: boolean,
  applicationOffer: OfferCalculationsType,
  ApplicantSSN?: string,
  CoApplicantSSN?: string,
  snapshots?: Snapshot[],
  userQuery?: any,
  dispatch?: Dispatch,
  downloadingDoc?: any,
  documentUploadStart?: any,
  documentUploadFinish?: any,
  uploadFile?: any,
  nonDisclosureContacts?: any[],
  isLocked?: boolean,
  shouldHideDTI?: boolean,
  alerts?: Alerts[],
  lifeOfLoanFloodZoneCertFeatureFlag?: boolean,
  hasOffer?: boolean,
  generateSampleContractDocs?: any
): DownloadItemsType[] {
  const applicant = _find(profiles, { position: 1 })
  const coApplicant = _find(profiles, { position: 2 })
  const creditReport = application?.creditReports.slice(-1).pop()
  const userEmail = userQuery?.data?.email || userQuery?.data?.username || ''
  const isLockedIE = application?.isLockedIe

  const third_party_report_permissions = getThirdPartyReportPermissions(userQuery, application)

  const hasClosingDates =
    !!application?.icsSignedOn &&
    !!application?.agreementEffectiveOn &&
    !!application?.wireToEscrowOn &&
    !!application?.recordingOn

  const isBlockedByAlert = alerts.some((alert) => docExpiredAlertMessages.includes(alert.message))

  const getInvestmentPdfName = (prefix: string, snapshot: Snapshot | null, isLockedIE: boolean) => {
    if (!showIEButton && prefix === 'IE') {
      return 'Investment Estimate'
    }

    if (!showICSButton && prefix === 'ICS') {
      return 'Closing Statement'
    }

    if (!snapshot || isLockedIE) return prefix

    return `${prefix} - ${formatDate(new Date(snapshot.created_at), 'MM/dd/yy - hh:mm:ss aa')}`
  }

  const getInvestmentPdfUrl = (prefix: string) => {
    if (prefix === 'IE') {
      return `${baseQueryConfig.baseUrl}/v1/application/${application['externalId']}/${
        showIEButton ? 'investment-estimate-snapshot' : 'investment-estimate'
      }/pdf`
    }

    if (prefix === 'DSCR') {
      return `${baseQueryConfig.baseUrl}/v1/application/${application['externalId']}/dscr-overview/pdf`
    }

    if (prefix === 'DTI') {
      return `${baseQueryConfig.baseUrl}/v1/application/${application['externalId']}/dti-overview/pdf`
    }

    return `${baseQueryConfig.baseUrl}/v1/application/${application['externalId']}/${
      showICSButton ? 'closing-statement-snapshot' : 'closing-statement'
    }/pdf`
  }

  const getInvestmentFilename = (prefix: string, basename: string, snapshot: Snapshot | null) => {
    if (prefix === 'IE') {
      return `${basename} - ${formatDate(
        showIEButton && snapshot ? new Date(snapshot.created_at) : new Date(),
        'yyyy-MM-dd'
      )}.pdf`
    }

    return `${basename} - ${formatDate(
      showICSButton && snapshot ? new Date(snapshot.created_at) : new Date(),
      'yyyy-MM-dd'
    )}.pdf`
  }

  const getNonDisclosurePDFs = async () => {
    try {
      if (downloadingDoc) {
        downloadingDoc(true)
      }

      const headers = localStorage.getItem('jwt')
        ? {
            'content-type': 'multipart/form-data',
            Authorization: `JWT ${localStorage.getItem('jwt')}`,
          }
        : {
            'content-type': 'multipart/form-data',
          }

      const files = []

      await Promise.all(
        nonDisclosureContacts.map(async (contact) => {
          const { data } = await axios.get(
            `${baseQueryConfig.baseUrl}/v1/application/${application['externalId']}/non-applicant-disclosure/${contact.uuid}/pdf/`,
            {
              headers,
              responseType: 'blob',
              withCredentials: true,
            }
          )
          const profileType = contact.profile_type === ProfileTypes.CO_OWNER ? 'Co-Owner' : 'NOO'

          const date = new Date().setHours(24, 0, 0, 0)
          const file = new File(
            [data],
            `Disclosure-Non-Applicant Disclosure-${
              contact.profile.full_name
            } (${profileType})-${format(date, 'yyyy-MM-dd')}.pdf`,
            { type: 'application/pdf' }
          )

          const payload = {
            application_profile: applicant.id,
            file_name: file.name,
            type: DOCUMENT_TYPE.APPLICATION,
            category: DOCUMENT_CATEGORY.DISCLOSURE,
            kind: 'disclosure--non-applicant-disclosure',
            tag: DOCUMENT_TAG.DEAL_FILES,
            source: DOCUMENT_SOURCE.MAX_SYSTEM,
          }
          const { url, uuid } = await documentUploadStart(payload).unwrap()
          await uploadFile({ url, file })
          const uploadedDoc = await documentUploadFinish(uuid).unwrap()
          files.push(uploadedDoc)
        })
      ).then(() => {
        openFilesInMaxViewer(application.externalId, files)
      })
    } finally {
      if (downloadingDoc) {
        downloadingDoc(false)
      }
    }
  }

  const getDisabledMessage = (
    prefix: string,
    hasSnapshot: boolean,
    hasOffer: boolean,
    isLockedIE = false
  ) => {
    if (!hasOffer) return 'Could not create an offer for this application'
    if (isBlockedByAlert) return 'Disabled due to expired documents'
    if (isLockedIE && !shouldHideDTI)
      return 'Escalations must be approved first to allow IE generation, DTI must be inferior to 45% and property usage cannot be Investment, Net Cash Proceeds cannot be negative.'
    if (isLockedIE && shouldHideDTI)
      return 'Escalations must be approved first to allow IE generation and property usage cannot be Investment, Net Cash Proceeds cannot be negative.'
    if (prefix === 'IE' && showIEButton && !hasSnapshot) return 'A snapshot was not found'
    if (prefix === 'ICS' && showICSButton && !hasSnapshot) return 'A snapshot was not found'

    if (prefix === 'DOC_DRAW' && !hasClosingDates) return 'Closing dates must be populated'
    if (prefix === 'DOC_DRAW' && !hasClosingDates) return 'This feature is not available yet'
    return ''
  }

  const getDisableDisclosure = () => {
    if (nonDisclosureContacts?.length === 0) return true
    for (let i = 0; i < nonDisclosureContacts?.length; i++) {
      if (!nonDisclosureContacts[i].profile.email) return true
    }
    return false
  }

  const getDisabledDisclosureMessage = () => {
    if (nonDisclosureContacts?.length === 0) return 'No NOO or Co-Owner contact'
    for (let i = 0; i < nonDisclosureContacts?.length; i++) {
      if (!nonDisclosureContacts[i].profile.email) return 'Email is required in all contacts'
    }
    return ''
  }

  const reImportDocusignParams = new URLSearchParams({
    opportunityId: `${application?.outreachOpportunityId || ''}`,
    email: `${userEmail}`,
  })
  const reImportDocusign = new URL(process.env.NEXT_PUBLIC_RE_IMPORT_DOCUSIGN_APPLICATION)
  reImportDocusign.search = reImportDocusignParams.toString()

  const reRunAVMSSearchParams = new URLSearchParams({
    opportunityId: `${application?.outreachOpportunityId || ''}`,
    addressStreet: `${application?.property?.address?.street || ''}`,
    addressCity: `${application?.property?.address?.city || ''}`,
    addressState: `${application?.property?.address?.state || ''}`,
    addressPostal: `${application?.property?.address?.zipCode || ''}`,
    email: `${userEmail}`,
  })
  const reRunAVMS = new URL(process.env.NEXT_PUBLIC_RE_RUN_AVMS)
  reRunAVMS.search = reRunAVMSSearchParams.toString()

  const enableDownload = applicationOffer?.selectedOffer?.offers ?? null
  const snapshotIE =
    showIEButton &&
    snapshots &&
    snapshots.find((snapshot) => snapshot.name === 'investment_estimate')
  const snapshotICS =
    showICSButton &&
    snapshots &&
    snapshots.find((snapshot) => snapshot.name === 'closing_statement')

  const reImportCreditTradelinesSearchParams = new URLSearchParams({
    opportunityId: `${application?.outreachOpportunityId || ''}`,
    fileNumber: `${creditReport?.fileNumber || ''}`,
    date: `${creditReport?.reportGeneratedAt || ''}`,
    addressLine1: `${application?.property?.address?.street || ''}`,
    addressCity: `${application?.property?.address?.city || ''}`,
    addressState: `${application?.property?.address?.state || ''}`,
    addressPostal: `${application?.property?.address?.zipCode || ''}`,
    applicantFirstName: `${applicant?.profile?.first_name || ''}`,
    applicantLastName: `${applicant?.profile?.last_name || ''}`,
    applicantSocialSecurityNumber: `${ApplicantSSN || ''}`,
    coApplicantFirstName: `${coApplicant?.profile?.first_name || ''}`,
    coApplicantLastName: `${coApplicant?.profile?.last_name || ''}`,
    coApplicantSocialSecurityNumber: `${CoApplicantSSN || ''}`,
    email: `${userEmail}`,
  })
  const reImportCreditTradelines = new URL(process.env.NEXT_PUBLIC_RE_IMPORT_TRADELINES)
  reImportCreditTradelines.search = reImportCreditTradelinesSearchParams.toString()

  const openPullCreditModal = () => {
    dispatch(setPullCreditModalOpen(true))
    dispatch(setPullCreditModalAction(''))
    dispatch(setPullCreditModalUser(''))
  }

  return [
    {
      title: 'documents',
      label: 'Documents',
      icon: unlock,
      top: 0,
      options: [
        {
          title: DEAL_SUMMARY_DOCUMENTS.NON_APPLICANT_DISCLOSURE,
          label: 'Non-Applicant Disclosure',
          functionClick: true,
          onClick: getNonDisclosurePDFs,
          disabled: getDisableDisclosure(),
          disabledMessage: getDisabledDisclosureMessage(),
        },
        {
          title: DEAL_SUMMARY_DOCUMENTS.INVESTMENT_ESTIMATE,
          label: getInvestmentPdfName('IE', snapshotIE, isLockedIE),
          downloadUrl: getInvestmentPdfUrl('IE'),
          disabled:
            !enableDownload || isLockedIE || (showIEButton && !snapshotIE) || isBlockedByAlert,
          disabledMessage: getDisabledMessage(
            'IE',
            snapshotIE != null,
            enableDownload != null,
            isLockedIE
          ),
          fileName: getInvestmentFilename('IE', 'Disclosure - Investment Estimate', snapshotIE),
          documentType: 'investment-estimate',
        },
        {
          title: DEAL_SUMMARY_DOCUMENTS.CLOSING_STATEMENT,
          label: getInvestmentPdfName('ICS', snapshotICS, false),
          downloadUrl: getInvestmentPdfUrl('ICS'),
          disabled: !enableDownload || (showICSButton && !snapshotICS) || isBlockedByAlert,
          disabledMessage: getDisabledMessage('ICS', snapshotICS != null, enableDownload != null),
          fileName: getInvestmentFilename(
            'ICS',
            'Disclosure - Investment Closing Statement',
            snapshotICS
          ),
          documentType: 'closing-statement',
        },
        {
          title: DEAL_SUMMARY_DOCUMENTS.DOC_DRAW_BETA,
          label: 'Doc Draw',
          onClick: setDocDrawModalOpen(true),
          disabled: !hasClosingDates || isBlockedByAlert,
          disabledMessage: getDisabledMessage(
            'DOC_DRAW',
            snapshotICS != null,
            enableDownload != null
          ),
        },
        {
          title: DEAL_SUMMARY_DOCUMENTS.DSCR_OVERVIEW,
          label: 'DSCR Overview Doc',
          downloadUrl: getInvestmentPdfUrl('DSCR'),
          fileName: 'Underwriting - DSCR Summary.pdf',
          disabled: false,
        },
        ...(!shouldHideDTI
          ? [
              {
                title: DEAL_SUMMARY_DOCUMENTS.DTI_OVERVIEW,
                label: 'DTI Overview Doc',
                downloadUrl: getInvestmentPdfUrl('DTI'),
                fileName: 'Underwriting - DTI Summary.pdf',
                disabled: false,
              },
            ]
          : []),
        {
          title: DEAL_SUMMARY_DOCUMENTS.SAMPLE_CONTRACT_DOCS,
          label: 'Sample Contract Docs',
          onClick: () =>
            requestSampleContractDocs(application.externalId, generateSampleContractDocs),
          disabled: !hasOffer,
          disabledMessage: 'A valid offer is required',
        },
      ],
    },
    {
      title: 'third-party',
      label: 'Order 3rd-party reports',
      icon: cloud,
      top: 3.6,
      options: [
        {
          title: THIRD_PARTY_REPORTS.APPRAISAL_REPORT,
          label: 'Appraisal Report',
          onClick: setAppraisalOrderModalOpen(true),
          disabled: !third_party_report_permissions?.appraisal,
        },
        {
          title: THIRD_PARTY_REPORTS.AVM_REPORT,
          label: 'AVM Report',
          onClick: setAvmReportModalOpen(true),
          disabled: !third_party_report_permissions?.avm,
        },
        {
          title: THIRD_PARTY_REPORTS.CREDIT_REPORT,
          label: 'Credit Report',
          onClick: openPullCreditModal,
          functionClick: true,
          disabled: !third_party_report_permissions?.credit,
        },
        {
          title: THIRD_PARTY_REPORTS.PROPERTY_DETAILS,
          label: 'CoreLogic - Property Details',
          onClick: async () => requestPropertyDetails(application['externalId'], dispatch),
          disabled: !third_party_report_permissions?.propertyDetails,
        },
        {
          title: 'flood-report',
          label: 'Flood Report',
          disabled: !third_party_report_permissions?.flood,
          subOptions: [
            {
              title: THIRD_PARTY_REPORTS.PRELIMINARY_FLOOD_ZONE_CERT,
              label: 'Preliminary Flood Zone Cert',
              onClick: async () => requestPreliminaryFloodReport(application['externalId']),
              disabled: !third_party_report_permissions?.flood,
            },
            {
              title: THIRD_PARTY_REPORTS.LIFE_OF_LOAN_FLOOD_ZONE_CERT,
              label: 'Flood Zone Life of Loan Determination',
              onClick: () => requestFloodZoneDeterminationReport(application['externalId']),
              disabled:
                !third_party_report_permissions?.flood || !lifeOfLoanFloodZoneCertFeatureFlag,
            },
          ],
        },
        {
          title: THIRD_PARTY_REPORTS.TITLE_REPORT,
          label: 'Title Report',
          onClick: setTitleOrderDrawerOpen(true),
          disabled: !third_party_report_permissions?.title,
        },
      ],
    },
  ]
}
